import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { content } from '../data/content';
import { 
  FaWhatsapp, 
  FaEnvelope, 
  FaGlobe, 
  FaLinkedin, 
  FaFacebook, 
  FaXTwitter, 
  FaEye, 
  FaBullseye, 
  FaPalette,
  FaChartLine,
  FaTruckMedical,
  FaHospital,
  FaBed,
  FaUserNurse,
  FaFlask,
  FaXRay,
  FaWrench,
  FaPills,
  FaBriefcase,
  FaLink
} from 'react-icons/fa6';

const socialLinks = [
  { icon: <FaLinkedin />, key: 'linkedin', url: 'https://www.linkedin.com/' },
  { icon: <FaFacebook />, key: 'facebook', url: 'https://www.facebook.com/' },
  { icon: <FaXTwitter />, key: 'x', url: 'https://x.com/' },
];

const LandingPage = () => {
  const { language, direction } = useLanguage();
  const c = content[language];
  const features = c.features;
  const vision = c.vision;
  
  // highlights: استخدم الإنجليزية أو العربية حسب اللغة
  const highlightsList = language === 'en'
    ? content.en.features.map(f => f.highlights)
    : content.ar.features.map(f => f.highlights);

  const getIconComponent = (iconName) => {
    const iconMap = {
      FaChartLine: FaChartLine,
      FaAmbulance: FaTruckMedical,
      FaHospital: FaHospital,
      FaBed: FaBed,
      FaUserNurse: FaUserNurse,
      FaFlask: FaFlask,
      FaXRay: FaXRay,
      FaTools: FaWrench,
      FaPills: FaPills,
      FaBriefcase: FaBriefcase,
      FaLink: FaLink
    };
    return iconMap[iconName] || FaChartLine;
  };

  return (
    <section style={{ 
      direction, 
      minHeight: '80vh', 
      position: 'relative', 
      overflow: 'hidden', 
      background: 'var(--color-bg)', 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      justifyContent: 'center' 
    }}>
      {/* خلفية SVG متحركة */}
      <svg className="animated-bg" width="100%" height="100%" style={{ position: 'absolute', top: 0, left: 0, zIndex: 0 }}>
        <circle cx="10%" cy="20%" r="60" fill="var(--color-accent)" opacity="0.13">
          <animate attributeName="cy" values="20%;80%;20%" dur="8s" repeatCount="indefinite" />
        </circle>
        <circle cx="80%" cy="70%" r="90" fill="var(--color-success)" opacity="0.10">
          <animate attributeName="cy" values="70%;30%;70%" dur="10s" repeatCount="indefinite" />
        </circle>
        <circle cx="50%" cy="10%" r="40" fill="var(--color-primary)" opacity="0.09">
          <animate attributeName="cy" values="10%;60%;10%" dur="12s" repeatCount="indefinite" />
        </circle>
      </svg>
      
      <div className="fade-in" style={{ 
        maxWidth: 700, 
        textAlign: 'center', 
        margin: '40px auto 24px auto', 
        position: 'relative', 
        zIndex: 2 
      }}>
        <img 
          src="/logo.png" 
          alt="logo" 
          style={{ 
            width: 110, 
            marginBottom: 18, 
            borderRadius: 24, 
            boxShadow: '0 8px 32px rgba(5, 102, 141, 0.4), 0 4px 16px rgba(0, 168, 150, 0.3)',
            border: '3px solid rgba(255, 255, 255, 0.3)',
            background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
            backdropFilter: 'blur(10px)',
            padding: '8px'
          }} 
        />
        <h1 style={{ 
          fontSize: '2.5em', 
          fontWeight: 900, 
          color: 'var(--color-primary)' 
        }}>
          {c.header.title}
        </h1>
        <h2 style={{ 
          fontSize: '1.3em', 
          fontWeight: 400, 
          color: 'var(--color-secondary)', 
          margin: '18px 0 0 0' 
        }}>
          {c.header.subtitle}
        </h2>
        <p style={{ 
          fontSize: '1.1em', 
          color: 'var(--color-text)', 
          margin: '24px 0 0 0', 
          opacity: 0.9 
        }}>
          {language === 'ar'
            ? 'نظام شفاء هو منصة متكاملة لإدارة المستشفيات تجمع بين الذكاء، الأمان، وسهولة الاستخدام. صُمم ليمنحك رؤية شاملة وتحكمًا فوريًا في كل تفاصيل العمل الطبي والإداري.'
            : 'Shifaa is an integrated hospital management platform combining intelligence, security, and ease of use. Designed to give you a comprehensive view and instant control over all medical and administrative operations.'}
        </p>
      </div>

      {/* قسم الرؤية */}
      <div className="vision-section slide-up" style={{ 
        maxWidth: 1200, 
        margin: '60px auto 40px auto', 
        position: 'relative', 
        zIndex: 2, 
        padding: '0 20px' 
      }}>
        <div style={{ textAlign: 'center', marginBottom: 50 }}>
          <div className="vision-title" style={{ marginBottom: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 18 }}>
            <FaEye style={{ 
              fontSize: '2.3em', 
              color: 'var(--color-accent)', 
              filter: 'drop-shadow(0 2px 8px rgba(2,195,154,0.18))',
              marginRight: direction === 'rtl' ? 0 : 18, 
              marginLeft: direction === 'rtl' ? 18 : 0 
            }} />
            <h2 style={{ 
              fontSize: '2.8em', 
              fontWeight: 900, 
              margin: 0,
              background: 'linear-gradient(90deg, #05668D 10%, #00A896 90%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              textFillColor: 'transparent',
              letterSpacing: '1px',
              textShadow: '0 4px 18px rgba(2,195,154,0.13), 0 1px 0 #fff',
              lineHeight: 1.1
            }}>
              {vision.vision_title}
            </h2>
          </div>
          <p style={{ 
            fontSize: '1.2em', 
            color: 'var(--color-text)', 
            lineHeight: 1.8, 
            maxWidth: 900, 
            margin: '0 auto', 
            opacity: 0.9 
          }}>
            {vision.statement}
          </p>
        </div>

        {/* الأهداف */}
        <div style={{ marginBottom: 60 }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            marginBottom: 30 
          }}>
            <FaBullseye style={{ 
              fontSize: '1.8em', 
              color: 'var(--color-success)', 
              marginRight: direction === 'rtl' ? 0 : 12, 
              marginLeft: direction === 'rtl' ? 12 : 0 
            }} />
            <h3 style={{ 
              fontSize: '2em', 
              fontWeight: 700, 
              color: 'var(--color-secondary)', 
              margin: 0 
            }}>
              {language === 'ar' ? 'أهدافنا' : 'Our Goals'}
            </h3>
          </div>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
            gap: 24, 
            marginTop: 30 
          }}>
            {vision.goals.map((goal, index) => (
              <div key={index} className="goal-card animated-card" style={{
                borderRadius: 16,
                padding: 28,
                transition: 'all 0.3s ease',
                transform: `translateY(${index % 2 === 0 ? 0 : 15}px)`,
                position: 'relative',
                overflow: 'hidden'
              }}>
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 4,
                  background: 'linear-gradient(90deg, var(--color-accent), var(--color-success))'
                }} />
                <h4 style={{ 
                  fontSize: '1.3em', 
                  fontWeight: 700, 
                  color: 'var(--color-primary)', 
                  margin: '0 0 12px 0' 
                }}>
                  {goal.title}
                </h4>
                <p style={{ 
                  fontSize: '1em', 
                  color: 'var(--color-text)', 
                  lineHeight: 1.6, 
                  margin: 0 
                }}>
                  {goal.description}
                </p>
              </div>
            ))}
          </div>
        </div>



        {/* الشعار */}
        <div style={{ textAlign: 'center', marginTop: 40 }}>
          <div className="slogan-card animated-card" style={{
            borderRadius: 20,
            padding: '32px 40px',
            transition: 'all 0.3s ease',
            display: 'inline-block',
            background: 'linear-gradient(135deg, #05668D 0%, #028090 50%, #00A896 100%)',
            border: '3px solid rgba(255, 255, 255, 0.4)',
            boxShadow: '0 8px 32px rgba(5, 102, 141, 0.4), 0 4px 16px rgba(0, 168, 150, 0.3)',
            backdropFilter: 'blur(15px)'
          }}>
            <h3 style={{ 
              fontSize: '2em', 
              fontWeight: 900, 
              color: 'white', 
              margin: 0, 
              textShadow: '0 2px 8px rgba(0,0,0,0.3)',
              letterSpacing: '0.5px',
              lineHeight: '1.3'
            }}>
              {vision.slogan}
            </h3>
          </div>
        </div>
      </div>

      {/* تأثير بارالكس للميزات */}
      <div className="features-list parallax center-features" style={{ 
        margin: '32px 0', 
        maxWidth: 1200, 
        position: 'relative', 
        zIndex: 2, 
        justifyContent: 'center', 
        alignItems: 'center' 
      }}>
        {features.map((feature, i) => {
          const IconComponent = getIconComponent(feature.icon);
          return (
            <div key={feature.id} className="feature-item slide-up animated-card" style={{ 
              minWidth: 260, 
              margin: '0 12px', 
              transform: `translateY(${i % 2 === 0 ? 0 : 20}px)`,
              borderRadius: 18,
              padding: '36px 24px 30px 24px',
              textAlign: 'center',
              position: 'relative',
              overflow: 'hidden'
            }}>
              <div className="feature-icon" style={{ fontSize: '2.2em' }}>
                <IconComponent style={{ width: '2.5rem', height: '2.5rem', color: 'white' }} />
              </div>
              <h3>{feature.title}</h3>
              <p style={{ fontSize: '1em', minHeight: 60 }}>{feature.description}</p>
              <ul style={{ textAlign: direction === 'rtl' ? 'right' : 'left', margin: '12px 0' }}>
                {highlightsList[i].map((h, j) => (
                  <li key={j} style={{ marginBottom: 4, color: 'var(--color-success)' }}>{h}</li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
      
      {/* أيقونات تواصل اجتماعي */}
      <div style={{ 
        margin: '32px 0 20px 0', 
        display: 'flex', 
        gap: 8, 
        flexWrap: 'wrap', 
        justifyContent: 'center', 
        alignItems: 'center', 
        position: 'relative', 
        zIndex: 2 
      }}>
        {c.contact.whatsapp && (
          <a 
            href={`https://wa.me/${c.contact.whatsapp}`} 
            className="contact-icon" 
            title="WhatsApp" 
            target="_blank" 
            rel="noopener noreferrer"
          >
            <FaWhatsapp />
          </a>
        )}
        {socialLinks.map(s => (
          <a 
            key={s.key} 
            href={s.url} 
            className="contact-icon" 
            title={s.key.charAt(0).toUpperCase() + s.key.slice(1)} 
            target="_blank" 
            rel="noopener noreferrer"
          >
            {s.icon}
          </a>
        ))}
        {c.contact.email && (
          <a 
            href={`mailto:${c.contact.email}`} 
            className="contact-icon" 
            title="Email"
          >
            <FaEnvelope />
          </a>
        )}
        {c.contact.website && (
          <a 
            href={c.contact.website} 
            className="contact-icon" 
            title="Website" 
            target="_blank" 
            rel="noopener noreferrer"
          >
            <FaGlobe />
          </a>
        )}
      </div>
    </section>
  );
};

export default LandingPage; 